import { shownAddress } from "./shown.ts";

// A crash in a feedback report (features/feedback.md, "What helps find it"; DESIGN.md, the log row "A crash in the
// report"): what a page that broke (the app's error page, a view or the map that broke: web/src/crash.tsx) sends
// with a Report this while the form's switch is on. The error's message, up to 500 characters, and where in Tripmo's
// code it happened: its stack trimmed to the frames in Tripmo's own scripts, up to 10, each file by its path on
// Tripmo's site without its query. The page makes it (crashOf) and the server cleans it again
// (./diagnostics.ts: crashMessage, crashFrame), whatever the page sent. Imports only ./shown.ts, which imports
// nothing: the web view bundles it.

export const MAX_CRASH_MESSAGE = 500;
export const MAX_CRASH_FRAMES = 10;
// A frame longer than this is no frame of Tripmo's (a minified name is a few letters).
const MAX_FRAME = 300;
const MAX_NAME = 120;

export interface Crash {
  // "TypeError: Cannot read properties of undefined (reading 'map')"
  message: string;
  // "Rate (/assets/index-Bx1c2.js:40:1234)", innermost first.
  stack: string[];
}

// The secret parts of a link (./shown.ts) wherever one sits in some words: a message can quote the address it failed
// on. A path's own secrets (a join link's or a pad's code, an upload's token) become "…", as in an address.
const SECRET_IN_TEXT = /(^|[\s"'`(=]|https?:\/\/[^/\s"'`]+)(\/(?:api\/)?(?:j|join|p|put)\/)[^/?#\s"'`)]+/g;

// A crash's message as a report keeps it: on one line, no secret a link in it carried, 500 characters at most.
export function crashMessage(message: string): string {
  return message.replace(/\s+/g, " ").trim().replace(SECRET_IN_TEXT, "$1$2…").slice(0, MAX_CRASH_MESSAGE);
}

// The libraries' own chunks in the built web view (web/vite.config.ts, codeSplitting's groups): React's and MapLibre's
// frames say nothing of where in Tripmo's code it broke, and would crowd its own out of the 10.
const LIBRARY = /^\/assets\/(?:react|maplibre|maplibre-gl-worker|rolldown-runtime)-[^/]*$/;

// One frame as kept, from its function's name (none: anonymous) and its script's address: only a script on
// Tripmo's own site (`origin`, the page's; none on the server, where the page already made it a path), by its path
// without query or hash, never a library's file a dev server serves on its own (/node_modules/, /@vite/...).
function place(name: string | undefined, url: string, line: string, column: string, origin?: string): string | null {
  let path = url;
  if (origin && path.startsWith(`${origin}/`)) path = path.slice(origin.length);
  path = path.replace(/[?#].*$/, "");
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  if (path.includes("/node_modules/") || path.startsWith("/@") || LIBRARY.test(path)) return null;
  const at = `${shownAddress(path)}:${line}:${column}`;
  const fn = name?.replace(/\s+/g, " ").trim().slice(0, MAX_NAME);
  const frame = fn ? `${fn} (${at})` : at;
  return frame.length <= MAX_FRAME ? frame : null;
}

// A browser's own frame: Chrome's "    at Rate (https://…/assets/index-Bx.js?v=1:40:1234)" or "    at https://…:1:2";
// Firefox's and Safari's "Rate@https://…:40:1234" or "@https://…:1:2".
const V8 = /^\s*at (?:(.+?) \()?(\S+?):(\d+):(\d+)\)?\s*$/;
const GECKO = /^\s*([^@]*)@(\S+?):(\d+):(\d+)\s*$/;

// What the page sends from an error it caught: its message, and the frames of its stack in Tripmo's own scripts.
export function crashOf(error: unknown, origin: string): Crash {
  const raw =
    error instanceof Error
      ? error.message
        ? `${error.name}: ${error.message}`
        : error.name
      : typeof error === "string"
        ? error
        : String(error);
  const stack: string[] = [];
  const said = error instanceof Error && typeof error.stack === "string" ? error.stack : "";
  // One browser's shape throughout: Chrome's stack starts with the message, which mustn't pass for a frame.
  const shape = /^\s*at /m.test(said) ? V8 : GECKO;
  for (const line of said.split("\n")) {
    if (stack.length === MAX_CRASH_FRAMES) break;
    const m = line.match(shape);
    if (!m?.[2] || !m[3] || !m[4]) continue;
    const frame = place(m[1], m[2], m[3], m[4], origin);
    if (frame) stack.push(frame);
  }
  return { message: crashMessage(raw), stack };
}

// A frame as the page sent it ("Rate (/assets/index-Bx.js:40:1234)" or "/assets/index-Bx.js:40:1234"), checked
// again on the server: a frame of anything but a path on Tripmo's site, or in another shape, isn't kept.
const KEPT = /^(?:(.+?) \()?(\S+?):(\d+):(\d+)\)?$/;
export function crashFrame(frame: unknown): string | null {
  if (typeof frame !== "string" || frame.length > MAX_FRAME) return null;
  const m = frame.trim().match(KEPT);
  if (!m?.[2] || !m[3] || !m[4]) return null;
  // The parenthesis is the frame's own only around its place.
  if (!m[1] !== !frame.trim().endsWith(")")) return null;
  return place(m[1], m[2], m[3], m[4]);
}
